import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * The style vocabulary this application speaks.
 *
 * Every `styles={{ … }}` prop in the app is typed by `Styles` below, and the key
 * set is closed: a property that is not declared here is a compile error. That is
 * the point. It means the styling surface is an inventory we own rather than
 * whatever the current component library happens to accept, so replacing that
 * library is a rewrite of `toVendor` plus the `vendorStyles` sites, not a sweep
 * through the call sites.
 *
 * The names are deliberately standard CSS (`marginBottom`, not a bespoke token)
 * with two exceptions that earn their shorthand by volume: the spacing scale and
 * the colour roles. Both are ours: `2` means two steps on our spacing scale and
 * `text.secondary` means a role in our palette, neither of which is a CSS value.
 */

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/** A value, or that value per breakpoint. Breakpoint names are ours, not a vendor's. */
export type Responsive<T> = T | Partial<Record<Breakpoint, T>>;

/**
 * One step on the layout spacing scale. Steps, not pixels — `1` is one step.
 * A raw CSS length is still accepted for the cases that are genuinely absolute
 * (a 2px hairline offset, `auto`).
 */
export type Space = number | string;

export type Length = number | string;

/**
 * A role in the palette. Not a colour: the mapping from role to value lives in
 * the theme, so a palette swap moves every one of these at once.
 *
 * Kept closed so that a raw hex in a component fails to compile — the rule the
 * design system already asserts in prose ("components never define their own
 * colors") becomes a type error instead of a grep.
 */
export type ColorToken =
  | 'text.primary' | 'text.secondary' | 'text.disabled'
  | 'primary' | 'primary.main' | 'primary.light' | 'primary.dark' | 'primary.contrastText'
  | 'secondary' | 'secondary.main' | 'secondary.contrastText'
  | 'neutral' | 'neutral.main' | 'neutral.contrastText'
  | 'success' | 'success.main' | 'success.dark' | 'success.contrastText'
  | 'error' | 'error.main' | 'error.dark' | 'error.contrastText'
  | 'warning' | 'warning.main' | 'warning.dark' | 'warning.contrastText'
  | 'info' | 'info.main' | 'info.light' | 'info.dark' | 'info.contrastText'
  | 'background.default' | 'background.paper'
  | 'action.active' | 'action.hover' | 'action.disabled' | 'action.selected'
  | 'common.black' | 'common.white'
  | 'divider'
  | 'inherit' | 'transparent' | 'currentColor' | 'none';

/**
 * A colour-carrying property. Accepts a role, or an explicit CSS colour for the
 * places that legitimately compute one — a token derived through `withAlpha`, a
 * gradient, a value read from the theme in a callback.
 */
type Paint = ColorToken | (string & {});

/** Border shorthand: `1` is a hairline in the divider colour; a full CSS string still works. */
type Border = number | string;

/**
 * The declared property set, before responsiveness is layered on.
 *
 * Derived from the 746 style props that existed when the seam was introduced,
 * so it covers what the application actually writes and nothing speculative.
 * A property missing from this list is a deliberate conversation, not an
 * oversight to route around with `vendorStyles`.
 */
interface StyleBase extends Pick<
  CSSProperties,
  | 'alignItems' | 'alignSelf' | 'justifyContent' | 'justifyItems'
  | 'display' | 'flex' | 'flexDirection' | 'flexGrow' | 'flexShrink' | 'flexWrap'
  | 'gridColumn' | 'gridTemplateColumns'
  | 'position' | 'top' | 'right' | 'bottom' | 'left' | 'inset' | 'zIndex'
  | 'overflow' | 'overflowX' | 'overflowY' | 'overscrollBehaviorY'
  | 'scrollbarGutter' | 'resize'
  | 'fontFamily' | 'fontSize' | 'fontStyle' | 'fontWeight' | 'fontVariantNumeric'
  | 'lineHeight' | 'letterSpacing' | 'textAlign' | 'textDecoration' | 'textTransform'
  | 'textOverflow' | 'whiteSpace' | 'wordBreak'
  | 'borderRadius' | 'borderStyle' | 'borderWidth'
  | 'opacity' | 'userSelect'
  | 'objectFit' | 'transform' | 'transformOrigin' | 'transition' | 'willChange'
  | 'animation' | 'animationName' | 'animationDuration' | 'animationFillMode'
  | 'animationTimingFunction'
  | 'outline'
  | 'WebkitBackgroundClip' | 'WebkitTextFillColor'
> {
  width?: Length;
  minWidth?: Length;
  maxWidth?: Length;
  height?: Length;
  minHeight?: Length;
  maxHeight?: Length;

  color?: Paint;
  backgroundColor?: Paint;
  background?: Paint;
  borderColor?: Paint;
  borderLeftColor?: Paint;
  outlineColor?: Paint;

  /** Set from a condition often enough that the literal unions get in the way. */
  cursor?: string;
  pointerEvents?: string;
  visibility?: string;

  /** A number selects a step on the elevation scale; a string is a CSS shadow. */
  boxShadow?: number | string;

  border?: Border;
  borderTop?: Border;
  borderRight?: Border;
  borderBottom?: Border;
  borderLeft?: Border;

  marginTop?: Space;
  marginRight?: Space;
  marginBottom?: Space;
  marginLeft?: Space;

  /** Spacing scale. `m`/`p` all sides, `x` horizontal, `y` vertical. */
  m?: Space;
  mt?: Space;
  mr?: Space;
  mb?: Space;
  ml?: Space;
  mx?: Space;
  my?: Space;
  p?: Space;
  pt?: Space;
  pr?: Space;
  pb?: Space;
  pl?: Space;
  px?: Space;
  py?: Space;
  gap?: Space;
}

/** Every declared property, each independently settable per breakpoint. */
export type Styles = { [K in keyof StyleBase]?: Responsive<StyleBase[K]> };

/**
 * The named leak.
 *
 * Nested selectors, vendor component class names (`& .MuiDataGrid-cell`) and
 * theme callbacks cannot be expressed in `Styles` because they are not styling
 * — they are reaching into the current library's internals. Sixty places needed
 * that when the seam was built. They keep working, and they are greppable, which
 * is the whole reason they have their own prop instead of being allowed through
 * the front door.
 */
export type VendorStyles = SxProps<Theme>;

/** Out of sight, still read by a screen reader: the one place `clip` is needed. */
export const visuallyHidden: VendorStyles = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
};

/**
 * Translate this application's style vocabulary into what the current component
 * library wants.
 *
 * Thin today, and that is by design rather than by accident: the token names were
 * chosen to be ones the incumbent already understands, which made adopting the
 * seam a rename instead of a re-style. The value is not in what this function
 * computes, it is in there being exactly one of it.
 */
export function toVendor(styles?: Styles, vendorStyles?: VendorStyles): SxProps<Theme> | undefined {
  if (!styles && !vendorStyles) return undefined;
  if (!styles) return vendorStyles;
  if (!vendorStyles) return styles as SxProps<Theme>;
  return [styles as SxProps<Theme>, vendorStyles] as SxProps<Theme>;
}

/** Props every wrapper in `src/ui` accepts, so styling reads the same everywhere. */
export interface Styleable {
  styles?: Styles;
  vendorStyles?: VendorStyles;
}

/**
 * The same property names as `Styles`, available at runtime.
 *
 * Needed because some of the vendor's legacy prop bags — `primaryTypographyProps`
 * and friends — used to be handed to a component that accepted style properties
 * as props, and no longer are. Forwarding such a bag verbatim puts `whiteSpace`
 * on a DOM node, where React warns and the style does nothing. `wrap` uses this
 * set to route those keys into the slot's own style prop instead.
 */
export const STYLE_PROP_NAMES: ReadonlySet<string> = new Set([
  'alignItems', 'alignSelf', 'justifyContent', 'justifyItems',
  'display', 'flex', 'flexDirection', 'flexGrow', 'flexShrink', 'flexWrap',
  'gridColumn', 'gridTemplateColumns',
  'position', 'top', 'right', 'bottom', 'left', 'inset', 'zIndex',
  'overflow', 'overflowX', 'overflowY', 'overscrollBehaviorY',
  'scrollbarGutter', 'resize',
  'fontFamily', 'fontSize', 'fontStyle', 'fontWeight', 'fontVariantNumeric',
  'lineHeight', 'letterSpacing', 'textAlign', 'textDecoration', 'textTransform',
  'textOverflow', 'whiteSpace', 'wordBreak',
  'borderRadius', 'borderStyle', 'borderWidth',
  'opacity', 'cursor', 'pointerEvents', 'userSelect', 'visibility',
  'objectFit', 'transform', 'transformOrigin', 'transition', 'willChange',
  'animation', 'animationName', 'animationDuration', 'animationFillMode',
  'animationTimingFunction', 'boxShadow', 'outline',
  'WebkitBackgroundClip', 'WebkitTextFillColor',
  'width', 'minWidth', 'maxWidth', 'height', 'minHeight', 'maxHeight',
  'color', 'backgroundColor', 'background', 'borderColor', 'borderLeftColor',
  'outlineColor',
  'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
  'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'm', 'mt', 'mr', 'mb', 'ml', 'mx', 'my',
  'p', 'pt', 'pr', 'pb', 'pl', 'px', 'py', 'gap',
]);
